import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Spin } from 'antd'
import dayjs from 'dayjs'
import useDirectory from 'hooks/useDirectory'
import Api from 'services/api'
import { AlertCalendarEvent } from 'types/alert'
import { AnnotationType } from 'types/building'
import CommonFunction from 'utils/commonFunction'

import FolderIcon from '../building/buildingFileList/components/FolderIcon'

import ModalAnnotationFiles, {
  AnnotationFileItem
} from './ModalAnnotationFiles'

type Props = {
  events: AlertCalendarEvent[]
  isLoading: boolean
}

// Group events by date (dayjs format YYYY-MM-DD)
function groupEventsByDate(events: AlertCalendarEvent[]) {
  const groups: Record<
    string,
    { dateLabel: string; weekLabel: string; items: AlertCalendarEvent[] }
  > = {}

  const weekdays = ['日', '月', '火', '水', '木', '金', '土']

  events
    .slice()
    .sort((a, b) => (a.start > b.start ? 1 : -1))
    .forEach((ev) => {
      const date = dayjs(ev.start)
      const key = date.format('YYYY-MM-DD')
      if (!groups[key]) {
        groups[key] = {
          dateLabel: date.format('M月D日'),
          weekLabel: `${date.year()}年${date.format('M月, ')}${
            weekdays[date.day()]
          }`,
          items: []
        }
      }
      groups[key].items.push(ev)
    })

  return Object.entries(groups).map(([, v]) => v)
}

export default function AlertListView({ events, isLoading }: Props) {
  const { t } = useTranslation()
  const { getBuiildingType } = useDirectory()
  const groups = groupEventsByDate(events)

  // Modal state — display all files of the clicked annotation
  const [modalOpen, setModalOpen] = useState(false)
  const [annotationFiles, setAnnotationFiles] = useState<AnnotationFileItem[]>(
    []
  )
  const [modalBuildingId, setModalBuildingId] = useState<number | undefined>(
    undefined
  )
  const [isLoadingAnnotation, setIsLoadingAnnotation] = useState<
    string | number | null
  >(null) // annotationId currently loading — to show Spin on the correct button

  // Call annotation detail API → get all files[] → open Modal
  const handleOpen360 = async (
    annotationId: string | number,
    buildingId: number,
    floorId: number | undefined,
    pointId: string | undefined
  ) => {
    if (!annotationId || !buildingId) return
    setIsLoadingAnnotation(annotationId)
    try {
      const res = await Api.get<any>(`v1/annotations/${annotationId}`, {
        building_id: buildingId,
        ...(floorId ? { floor_id: floorId } : {}),
        ...(pointId ? { point_id: pointId } : {})
      })
      // files is an array of FilePointResource — display all in Modal
      const files: AnnotationFileItem[] = res?.files ?? []
      setAnnotationFiles(files)
      setModalBuildingId(buildingId)
      setModalOpen(true)
    } catch {
      // API error → still open Modal (empty) to not block user
      setAnnotationFiles([])
      setModalBuildingId(buildingId)
      setModalOpen(true)
    } finally {
      setIsLoadingAnnotation(null)
    }
  }

  if (isLoading) {
    return (
      <div className="alert-list-loading">
        <Spin size="large" />
      </div>
    )
  }

  if (groups.length === 0) {
    return (
      <div className="alert-list-empty">
        <span>今月のアラートはありません</span>
      </div>
    )
  }

  return (
    <div className="alert-list-view">
      {groups.map((group) => (
        <div
          key={group.items[0]?.start ?? group.dateLabel}
          className="alert-list-group"
        >
          {/* Date header */}
          <div className="alert-list-date-header">
            <span className="alert-list-date-day">
              {dayjs(group.items[0].start).date()}
            </span>
            <span className="alert-list-date-label">
              {dayjs(group.items[0].start).format('YYYY年M月')}
            </span>
          </div>

          {/* Events of this day */}
          <div className="alert-list-items">
            {group.items.map((ev) => {
              const startTime = dayjs(ev.start).format('H:mm')
              const endTime = ev.end ? dayjs(ev.end).format('H:mm') : null
              const timeRange = endTime ? `${startTime}〜${endTime}` : startTime
              const buildingId = ev.extendedProps?.buildingId as
                | number
                | undefined
              const floorId = ev.extendedProps?.floorId as number | undefined
              const folderId = ev.extendedProps?.folderId
              const childrenId = ev.extendedProps?.childrenId
              const annotationId = ev.extendedProps?.annotationId as
                | string
                | number
                | undefined
              const annotationType = ev.extendedProps?.annotationType as
                | number
                | undefined
              const pointId = ev.extendedProps?.pointId as string | undefined
              let label = ''

              if (childrenId) {
                label = CommonFunction.getBreadcrumbTextByChidType(
                  String(childrenId),
                  t,
                  getBuiildingType()
                )
              } else if (folderId) {
                label = CommonFunction.getBreadcrumbTextByFolderType(
                  String(folderId),
                  t
                )
              }

              const canOpen360 = !!(annotationId && buildingId)
              const isThisLoading = isLoadingAnnotation === annotationId

              const getAnnotationLabel = (type: number) => {
                switch (type) {
                  case AnnotationType.FIRE:
                    return '火災'
                  case AnnotationType.FLOOD:
                    return '水害'
                  case AnnotationType.EARTHQUAKE:
                    return '地震'
                  case AnnotationType.VOLCANO:
                    return '降灰'
                  case AnnotationType.LIFE_LINE:
                    return 'ライフライン'
                  case AnnotationType.MEMO:
                    return 'メモ'
                  case AnnotationType.ARCHITECTURE:
                    return '建築'
                  case AnnotationType.AIR_CONDITION:
                    return '空調衛生'
                  case AnnotationType.ELECTRICITY:
                    return '電気'
                  case AnnotationType.EQUIPMENT:
                    return '機材'
                  case AnnotationType.IMAGE:
                    return 'ピクチャ'
                  case AnnotationType.MOVIE:
                    return 'ムービー'
                  case AnnotationType.INFO:
                    return 'インフォ'
                  default:
                    return ''
                }
              }

              return (
                <div key={ev.id} className="alert-list-item">
                  {/* Time */}
                  <span className="alert-list-time">{timeRange}</span>

                  {/* Icon inline */}
                  {annotationType && (
                    <span className="alert-list-type-icon-wrapper flex items-center gap-2 min-w-[100px]">
                      <FolderIcon
                        type={annotationType as AnnotationType}
                        size="small"
                      />
                      <span className="alert-list-type-text whitespace-nowrap text-[13px] font-medium text-[#374151]">
                        {getAnnotationLabel(annotationType)}
                      </span>
                    </span>
                  )}

                  {/* Label plain text */}
                  {label && (
                    <span className="alert-list-type-label">{label}</span>
                  )}

                  {/* Title */}
                  <span className="alert-list-title">{ev.title}</span>

                  {canOpen360 && (
                    <button
                      type="button"
                      className="alert-list-360-btn"
                      disabled={isThisLoading}
                      onClick={() =>
                        handleOpen360(
                          annotationId,
                          buildingId,
                          floorId,
                          pointId
                        )
                      }
                    >
                      {isThisLoading ? (
                        <Spin size="small" />
                      ) : (
                        '▶ 360画像を表示'
                      )}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {/* Modal displaying all files of the annotation (from Annotation Detail API) */}
      <ModalAnnotationFiles
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        files={annotationFiles}
        buildingId={modalBuildingId}
      />
    </div>
  )
}
